import React from 'react';
import {
  SpeedDial as MuiSpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Backdrop,
  Fab,
  useTheme,
  Tooltip,
} from '@mui/material';
import { Add, Edit, Close } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledSpeedDial = styled(MuiSpeedDial)<{
  variant?: 'default' | 'extended';
  size?: 'small' | 'medium' | 'large';
}>(({ theme, variant = 'default', size = 'medium' }) => ({
  '& .MuiSpeedDial-fab': {
    ...(size === 'small' && {
      width: 40,
      height: 40,
    }),
    ...(size === 'large' && {
      width: 64,
      height: 64,
    }),
    ...(variant === 'extended' && {
      borderRadius: `calc(${theme.shape.borderRadius}px * 6)`,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      minWidth: 120,
    }),
  },
  '& .MuiSpeedDialAction-fab': {
    ...(size === 'small' && {
      width: 36,
      height: 36,
    }),
    ...(size === 'large' && {
      width: 48,
      height: 48,
    }),
  },
}));

export interface SpeedDialActionData {
  /**
   * Unique identifier
   */
  id: string;
  /**
   * Action icon
   */
  icon: React.ReactNode;
  /**
   * Action label/tooltip
   */
  label: string;
  /**
   * Click handler
   */
  onClick?: () => void;
  /**
   * Whether action is disabled
   */
  disabled?: boolean;
  /**
   * Custom color for the action
   */
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  /**
   * Whether to show tooltips on hover
   */
  showTooltip?: boolean;
}

export interface SpeedDialProps {
  /**
   * Speed dial actions
   */
  actions: SpeedDialActionData[];
  /**
   * Whether speed dial is open
   */
  open?: boolean;
  /**
   * Callback fired when speed dial should open/close
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Main FAB icon
   */
  icon?: React.ReactNode;
  /**
   * Open state icon
   */
  openIcon?: React.ReactNode;
  /**
   * Direction of the actions
   */
  direction?: 'up' | 'down' | 'left' | 'right';
  /**
   * Speed dial variant
   */
  variant?: 'default' | 'extended';
  /**
   * Size of the speed dial
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Color theme
   */
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  /**
   * Position on screen
   */
  position?: {
    bottom?: number;
    top?: number;
    left?: number;
    right?: number;
  };
  /**
   * Whether to show backdrop when open
   */
  showBackdrop?: boolean;
  /**
   * Label for extended variant
   */
  label?: string;
  /**
   * Whether speed dial is hidden
   */
  hidden?: boolean;
  /**
   * Whether to close on action click
   */
  closeOnActionClick?: boolean;
  /**
   * Custom styling
   */
  sx?: any;
}

export const SpeedDial: React.FC<SpeedDialProps> = ({
  actions,
  open: openProp,
  onOpenChange,
  icon = <Add />,
  openIcon = <Close />,
  direction = 'up',
  variant = 'default',
  size = 'medium',
  color = 'primary',
  position = { bottom: 16, right: 16 },
  showBackdrop = false,
  label,
  hidden = false,
  closeOnActionClick = true,
  sx,
}) => {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const theme = useTheme();
  
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  const handleActionClick = (action: SpeedDialActionData) => {
    action.onClick?.();
    if (closeOnActionClick) {
      handleOpenChange(false);
    }
  };

  const getActionColor = (actionColor?: SpeedDialActionData['color']) => {
    return actionColor || 'default';
  };

  return (
    <>
      {showBackdrop && (
        <Backdrop
          open={open}
          sx={{ zIndex: theme.zIndex.speedDial - 1 }}
          onClick={() => handleOpenChange(false)}
        />
      )}
      
      <StyledSpeedDial
        ariaLabel="speed-dial"
        open={open}
        onOpen={() => handleOpenChange(true)}
        onClose={() => handleOpenChange(false)}
        icon={
          variant === 'extended' && label ? (
            <Fab
              variant="extended"
              color={color}
              size={size}
              sx={{ 
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: theme.shadows[6],
                },
              }}
            >
              {open ? openIcon : icon}
              {!open && label}
            </Fab>
          ) : (
            <SpeedDialIcon icon={icon} openIcon={openIcon} />
          )
        }
        direction={direction}
        variant={variant}
        size={size}
        hidden={hidden}
        sx={{
          position: 'fixed',
          ...position,
          ...sx,
        }}
        FabProps={{
          color,
          size,
        }}
      >
        {actions.map((action) => (
          <SpeedDialAction
            key={action.id}
            icon={action.icon}
            tooltipTitle={action.label}
            onClick={() => !action.disabled && handleActionClick(action)}
            FabProps={{
              color: getActionColor(action.color),
              size: size === 'large' ? 'medium' : 'small',
              disabled: action.disabled,
            }}
            sx={{
              ...(action.disabled && {
                opacity: 0.5,
                pointerEvents: 'none',
              }),
            }}
          />
        ))}
      </StyledSpeedDial>
    </>
  );
}; 